import { z } from "zod";
import type { ReactNode } from "react";

// Option/key hygiene: lowercase alphanumeric + _/-, no leading underscore, ASCII.
const ID_REGEX = /^[a-z0-9][a-z0-9_-]*$/;
const SLUG_REGEX = /^[a-z0-9-]+$/;
const RESERVED_OPTION_VALUES = new Set(["advance"]); // synthetic info-screen advance id

// Optional outbound CTA on a result (e.g. "Descubrir el producto" -> PDP).
// https-only at the schema layer: blocks javascript:/data:/http: hrefs.
export const ResultCtaSchema = z.object({
  label: z.string().min(1),
  href: z.string().url().startsWith("https://"),
});
export type ResultCta = z.infer<typeof ResultCtaSchema>;

// Result archetype copy, co-located with each persona (Phase 1: no global registry).
export const ResultContentSchema = z.object({
  tagline: z.string().min(1),
  body: z.array(z.string().min(1)).min(1),
  cta: ResultCtaSchema.optional(),
});
export type ResultContent = z.infer<typeof ResultContentSchema>;

// A persona a quiz can route to. Persona keys reuse the option-id charset hygiene.
export const PersonaSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(ID_REGEX, "must be lowercase alphanumeric with _ or -, no leading underscore"),
  label: z.string().min(1),
  result: ResultContentSchema,
});
export type Persona = z.infer<typeof PersonaSchema>;

export const OptionSchema = z.object({
  value: z
    .string()
    .min(1)
    .max(64)
    .regex(ID_REGEX, "must be lowercase alphanumeric with _ or -, no leading underscore")
    .refine((v) => !RESERVED_OPTION_VALUES.has(v), { message: "'advance' is reserved" }),
  label: z.string().min(1),
  emoji: z.string().optional(),
  /**
   * Multi-select only: picking this option clears every other selection, and
   * picking any other option clears this one. For "None of these" answers.
   * Ignored by non-multi question types.
   */
  exclusive: z.boolean().optional(),
});
export type Option = z.infer<typeof OptionSchema>;

const baseFields = {
  id: z.string().min(1).regex(ID_REGEX),
  helper: z.string().optional(),
  whyWeAsk: z.string().optional(),
};

const SingleSchema = z.object({
  ...baseFields,
  type: z.literal("single"),
  prompt: z.string().min(1),
  options: z.array(OptionSchema).min(2),
});
const MultiSchema = z.object({
  ...baseFields,
  type: z.literal("multi"),
  prompt: z.string().min(1),
  options: z.array(OptionSchema).min(2),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(1).optional(),
});
const SliderSchema = z.object({
  ...baseFields,
  type: z.literal("slider"),
  prompt: z.string().min(1),
  min: z.number(),
  max: z.number(),
  step: z.number().positive().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
});
const TextSchema = z.object({
  ...baseFields,
  type: z.literal("text"),
  prompt: z.string().min(1),
  placeholder: z.string().optional(),
});
const NumberSchema = z.object({
  ...baseFields,
  type: z.literal("number"),
  prompt: z.string().min(1),
  unit: z.string().optional(),
  placeholder: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  defaultValue: z.number().optional(),
  optional: z.boolean().optional(),
});
// Optional illustrated cards on an info screen (e.g. the AURAL "villains" grid).
// `image` is a path/URL the screen renders verbatim in an <img>.
export const InfoCardSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  image: z.string().min(1),
  imageAlt: z.string().optional(),
});
export type InfoCard = z.infer<typeof InfoCardSchema>;

// Optional social-proof quote — shown on the landing (first question) screen via
// meta.testimonial, and on any info screen via its `testimonial` field.
export const TestimonialSchema = z.object({
  quote: z.string().min(1),
  author: z.string().min(1),
  /** Optional 1–5 star rating rendered above the quote. */
  stars: z.number().int().min(1).max(5).optional(),
  /** Optional customer photo (path/URL), rendered verbatim in an <img> as a
   * circular avatar beside the quote. */
  avatar: z.string().min(1).optional(),
});
export type Testimonial = z.infer<typeof TestimonialSchema>;

const InfoSchema = z.object({
  ...baseFields,
  type: z.literal("info"),
  heading: z.string().min(1),
  body: z.array(z.string()).min(1),
  /** Optional single illustration (path/URL, rendered verbatim in an <img>)
   * shown between the heading and the body: a villain graphic on a bridge,
   * a customer photo on a proof card. For a GRID of captioned images use
   * `cards` instead. */
  image: z.string().min(1).optional(),
  imageAlt: z.string().optional(),
  cards: z.array(InfoCardSchema).optional(),
  /** Optional social-proof quote, rendered as a distinct testimonial card
   * (not a plain body paragraph) below the body. */
  testimonial: TestimonialSchema.optional(),
  autoAdvanceMs: z.number().int().min(0).optional(),
});

export const QuestionSchema = z.discriminatedUnion("type", [
  SingleSchema,
  MultiSchema,
  SliderSchema,
  TextSchema,
  NumberSchema,
  InfoSchema,
]);
export type Question = z.infer<typeof QuestionSchema>;

export const QuizMetaSchema = z.object({
  title: z.string().min(1),
  introHeadline: z.string().min(1),
  introBody: z.string().optional(),
  testimonial: TestimonialSchema.optional(),
  locale: z.enum(["en", "es"]).optional(),
  /**
   * Number of screens on any single path through the quiz. Defaults to
   * questions.length, which is only correct when every question is on every
   * path. Quizzes with mutually-exclusive branch variants (where the user sees
   * one of N alternatives) must declare this, or the denominator overcounts.
   */
  progressTotal: z.number().int().min(1).optional(),
});
export type QuizMeta = z.infer<typeof QuizMetaSchema>;

// The serializable shape validated by Zod at boot. branching/classifier are
// functions (validated behaviourally by validateQuiz, not by Zod).
export const QuizDataSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1).max(64).regex(SLUG_REGEX),
  tenant: z
    .string()
    .min(1)
    .max(64)
    .regex(ID_REGEX, "must be lowercase alphanumeric with _ or -, no leading underscore"),
  meta: QuizMetaSchema,
  questions: z.array(QuestionSchema).min(1),
  personas: z
    .array(PersonaSchema)
    .min(1)
    .refine((ps) => new Set(ps.map((p) => p.key)).size === ps.length, {
      message: "persona keys must be unique",
    }),
});
export type QuizData = z.infer<typeof QuizDataSchema>;

export type AnswerValue = string | string[] | number | null;
export type Answers = Record<string, AnswerValue>;

// A persona quiz: data + two pure functions.
export interface QuizDefinition extends QuizData {
  /** Pure: returns the next question id, or null when the question phase is done. */
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  /**
   * Pure: maps a completed answer set to exactly one declared persona key.
   * Return type is `string` (not a constrained union) because persona keys are
   * per-quiz data; validateQuiz() enforces that the returned key is declared.
   */
  classifier: (answers: Answers) => string;
  /**
   * Optional custom result surface (AURAL water report). When present, the
   * result phase renders this instead of <ResultScreen>. A function (like
   * branching/classifier) — NOT part of the Zod QuizDataSchema. Tenants that
   * omit it keep byte-identical behaviour.
   */
  renderResult?: (ctx: {
    answers: Answers;
    persona: Persona;
    /** Fire the funnel's cta_clicked event. Call it from the surface's "go to
     * offer" moment (a buy link, a claim-discount button) — fire-and-forget,
     * safe to call right before a redirect (keepalive transport). */
    trackCta?: () => void;
  }) => ReactNode;
  /**
   * Optional custom "building" (thinking) surface. When present, the building
   * phase renders this instead of <BuildingScreen>. The hook OWNS ITS TIMING:
   * it must call `onDone` to advance to the result. Quizzes that omit it keep
   * byte-identical behaviour (BuildingScreen + the `buildingMs` prop).
   */
  renderBuilding?: (ctx: { answers: Answers; onDone: () => void }) => ReactNode;
  /**
   * Optional raw CSS the engine injects ONCE, present on every phase it renders
   * (running / building / result). This is the home for CSS shared across a
   * quiz's custom surfaces — most importantly when renderBuilding and
   * renderResult use the same brand classes. Injecting inside the (lazy) result
   * funnel alone leaves the earlier building screen unstyled, because that
   * bundle has not loaded yet. Declare it here instead. Scoped to the widget's
   * shadow root. A raw string (like the render hooks) — NOT part of the Zod
   * QuizDataSchema. Quizzes whose custom CSS is only needed in the result phase
   * (no custom building screen) may keep code-splitting it inside their lazy
   * result surface instead.
   */
  styles?: string;
}

/** Identity helper that pins the type; boot-time Zod parse happens in QuizWidget. */
export function defineQuiz(def: QuizDefinition): QuizDefinition {
  return def;
}

export function getQuestion(quiz: QuizDefinition, id: string): Question | undefined {
  return quiz.questions.find((q) => q.id === id);
}

export function firstQuestionId(quiz: QuizDefinition): string {
  const first = quiz.questions[0];
  if (!first) throw new Error("quiz has no questions");
  return first.id;
}

/** Stable string id for an answer value (analytics + tests). Multi joins with '+'. */
export function answerToId(value: AnswerValue): string {
  if (value === null) return "";
  if (Array.isArray(value)) return value.join("+");
  return String(value);
}
